import { EmptyState } from 'components/empty.state';
import { HistoryOutlined } from '@mui/icons-material';
import React, { Fragment, useMemo } from 'react';

import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Typography, Box, SxProps, Theme } from '@mui/material';
import { toast } from 'react-toastify';
import { LoadingBox } from 'components/loading.box';
import { BusinessCenterOutlined, CalendarTodayOutlined, FmdGoodOutlined } from '@mui/icons-material';
import { MissionCard } from 'components/theme/cards/mission.card';
import { useGetContracts } from 'core/contracts/hooks';

interface Props {
    tempWorker: any;
}

interface CurrentContractProps {
    contract: any;
}

interface CurrentContractsProps {
    contracts: any[];
    sx?: SxProps<Theme>;
}


const CurrentContract = ({contract}: CurrentContractProps) => {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: '0.5rem' }}>
            <MissionCard color='info'>
                <BusinessCenterOutlined />
                <Typography sx={{fontWeight: 500, fontSize: '0.875rem'}}>{contract.qualification_free_form || contract.qualification}</Typography>
            </MissionCard>
            <MissionCard>
                <FmdGoodOutlined />
                <Typography sx={{fontWeight: 500, fontSize: '0.875rem'}}>{contract.company_name}</Typography>
            </MissionCard>
            <MissionCard>
                <CalendarTodayOutlined />
                <Typography sx={{fontWeight: 500, fontSize: '0.875rem'}}>{contract.start_date} - {contract.end_date}</Typography>
            </MissionCard>
        </Box>
    );
};

const CurrentContracts = ({contracts, sx}: CurrentContractsProps) => {
    return (
        <Box sx={sx}>
            <Typography variant='h5' sx={{mb: '0.75rem'}}>En Cours</Typography>
            {contracts.map((contract) => (
                <CurrentContract key={contract.id} contract={contract} />
            ))}
        </Box>
    );
};

const ContractsPartial = ({tempWorker}: Props) => {
    const {data: contracts, isLoading, isError} = useGetContracts(tempWorker.id, {onError: (err) => toast.error('Une erreur est survenue')});

    const currentContracts = useMemo(() => {
        return (contracts || []).filter((el: any) => el.current);
    }, [contracts]);

    const nonCurrentContracts = useMemo(() => {
        return (contracts || []).filter((el: any) => !el.current);
    }, [contracts]);

    if (isLoading) { return (<LoadingBox />); }
    if (isError || contracts === undefined) { return (<Typography variant='subtitle1' color='error'>Une erreur est survenue</Typography>); }
    if (contracts.length === 0) { return (<EmptyState icon={<HistoryOutlined />} title='Aucune mission' description="Les missions passées et en cours de cet intérimaire s'afficheront ici." />); }

    return (
        <Fragment>
            {currentContracts.length > 0 && (<CurrentContracts sx={{mb: '2rem'}} contracts={currentContracts} />)}
            <Typography variant='h5' sx={{mb: '0.75rem'}}>Terminées</Typography>
            {nonCurrentContracts.length === 0 && (<EmptyState icon={<HistoryOutlined />} title='Aucune mission terminée' sx={{ py: 3 }} />)}
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nom</TableCell>
                            <TableCell>Entreprise</TableCell>
                            <TableCell align="right">Dates</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {nonCurrentContracts.map((contract: any) => (
                            <TableRow key={contract.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell component="th" scope="row">{contract.qualification_free_form || contract.qualification}</TableCell>
                                <TableCell component="th" scope="row">{contract.company_name}</TableCell>
                                <TableCell align="right">{contract.start_date} - {contract.end_date}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Fragment>
    );
};

export default ContractsPartial;